import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, List, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import BlockRenderer from "../../components/course/BlockRenderer";
import { useCourseModules } from "../../contexts/CourseContext";
import { usePageTitle } from "../../hooks/usePageTitle";
import { HeadingEntry } from "../../utils/extractHeadings";
import { LessonPageSkeleton } from "../../components/skeletons/PageSkeletons";
import "./course.css";

const ArticleDetailPage: React.FC = () => {
  const { articleSlug = "" } = useParams();
  const { articles, loading } = useCourseModules();

  const article = articles.find((a) => a.slug === articleSlug);

  const orderedArticles = articles.filter((a) => a.published).sort((a, b) => a.order - b.order);
  const articleIndex = orderedArticles.findIndex((a) => a.slug === articleSlug);
  const adjacent = {
    previous: articleIndex > 0 ? orderedArticles[articleIndex - 1] : undefined,
    next: articleIndex >= 0 && articleIndex < orderedArticles.length - 1 ? orderedArticles[articleIndex + 1] : undefined,
  };

  usePageTitle(article ? article.title : "Article");

  const [contentHeadings, setContentHeadings] = useState<HeadingEntry[]>([]);
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem("course-reading-focus-mode") === "true";
    } catch {
      return false;
    }
  });

  const toggleFocusMode = () => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("course-reading-focus-mode", String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [activeHash, setActiveHash] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.hash.replace("#", "") : ""
  );

  useEffect(() => {
    const onHashChange = () => setActiveHash(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Scrollspy: highlight whichever section is currently near the top of the
  // viewport as the reader scrolls, not just the one they last clicked.
  useEffect(() => {
    const elements = contentHeadings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length === 0) return;
        const topMost = visible.reduce((a, b) => (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
        setActiveHash(topMost.target.id);
      },
      { rootMargin: "-110px 0px -70% 0px", threshold: 0 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [contentHeadings]);

  const scrollToHeading = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.pushState(null, "", `#${id}`);
    setActiveHash(id);
    setMobileTocOpen(false);

    // Wait for the state update above to commit and paint before grabbing
    // the element — otherwise a re-render triggered by setActiveHash can
    // swap the DOM node out from under an in-flight scrollIntoView call.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.focus({ preventScroll: true });
        }
      });
    });
  };

  if (loading) {
    return (
      <LessonPageSkeleton label="Loading article…" withMeta={false} />
    );
  }

  if (!article || !article.published) {
    return <Navigate to="/writing" replace />;
  }

  return (
    <article className="course-shell lesson-shell">
      <header className="lesson-header">
        <div className="course-wrap lesson-header__inner">
          <Link className="course-back-link" to="/writing">
            <ArrowLeft aria-hidden="true" size={17} /> All writing
          </Link>
          <div className="lesson-header__title">
            <div>
              <p>Article</p>
              <h1>{article.title}</h1>
            </div>
          </div>
          {article.tagline && <p className="lesson-deck">{article.tagline}</p>}
        </div>
      </header>

      <button
        type="button"
        className="lesson-focus-toggle"
        onClick={toggleFocusMode}
        aria-pressed={focusMode}
        title={focusMode ? "Show table of contents" : "Hide table of contents (full-screen reading)"}
      >
        {focusMode ? (
          <PanelLeftOpen aria-hidden="true" size={18} />
        ) : (
          <PanelLeftClose aria-hidden="true" size={18} />
        )}
        <span className="sr-only">{focusMode ? "Show table of contents" : "Hide table of contents"}</span>
      </button>

      <button
        type="button"
        className="lesson-toc-drawer-toggle"
        onClick={() => setMobileTocOpen(true)}
        aria-label="Open table of contents"
      >
        <List aria-hidden="true" size={17} /> Contents
      </button>

      {mobileTocOpen && (
        <div className="lesson-toc-backdrop" onClick={() => setMobileTocOpen(false)} aria-hidden="true" />
      )}

      <div className={`course-wrap lesson-layout ${focusMode ? "lesson-layout--focus" : ""}`}>
        <aside
          className={`lesson-aside ${focusMode ? "is-focus-hidden" : ""} ${mobileTocOpen ? "is-open" : ""}`}
          aria-label="Article contents"
        >
          <div className="lesson-aside__mobile-header">
            <p>In this article</p>
            <button type="button" onClick={() => setMobileTocOpen(false)} aria-label="Close table of contents">
              <X aria-hidden="true" size={18} />
            </button>
          </div>
          <p className="lesson-aside__desktop-label">In this article</p>
          <nav>
            {contentHeadings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                className={`lesson-aside__link lesson-aside__link--level-${heading.level} ${
                  activeHash === heading.id ? "is-active" : ""
                }`}
                onClick={scrollToHeading(heading.id)}
              >
                {heading.text}
              </a>
            ))}
          </nav>
        </aside>

        <div className="lesson-content">
          {article.coverImageUrl && (
            <div className="course-hero__artifact course-hero__artifact--cover" aria-label="Article cover">
              <img src={article.coverImageUrl} alt="" />
              {article.coverImageIsPlaceholder && <p className="course-hero__cover-note">Placeholder cover</p>}
            </div>
          )}

          <div id="content">
            <BlockRenderer blocks={article.blocks} scopeId={`article/${articleSlug}`} onHeadingsChange={setContentHeadings} />
          </div>

          <nav className="lesson-pagination" aria-label="Article navigation">
            {adjacent.previous ? (
              <Link to={`/writing/articles/${adjacent.previous.slug}`}>
                <ArrowLeft aria-hidden="true" size={18} />
                <span><small>Previous</small>{adjacent.previous.title}</span>
              </Link>
            ) : <span />}
            {adjacent.next ? (
              <Link to={`/writing/articles/${adjacent.next.slug}`}>
                <span><small>Next</small>{adjacent.next.title}</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            ) : (
              <Link to="/writing">
                <span><small>Continue</small>Back to writing</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            )}
          </nav>
        </div>
      </div>
    </article>
  );
};

export default ArticleDetailPage;
